// --------------------------------------------------------------
// Created On: 2025-03-04
// Author: Hannah Vaughan
//
// Last Modified: 2025-11-20
// Modified By: Hannah Vaughan
//
// Copyright 2025 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { useRef } from "react";
import styles from "./SalesOrderDetails.module.scss";
import {
  CORNELL_ADDRESS_LINE_ONE,
  CORNELL_ADDRESS_LINE_THREE,
  CORNELL_ADDRESS_LINE_TWO,
  CORNELL_WEB_SITE,
  SALES_CONTACT_EMAIL_ADDRESS,
  SALES_CONTACT_FAX_NUMBER,
  SALES_CONTACT_PHONE_NUMBER,
  SALES_ORDER_NOT_PLACED_DISCLAIMER,
  SHOW_SALES_ORDERS_NOT_PLACED_DISCLAIMER,
} from "../../constants/miscellaneous";
import stringIsValidNumber from "../../utilities/stringIsValidNumber";
import { Link } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import formatDashTimeString from "../../utilities/formatDashTimeString";

// Displays information on a completed sales order that is visible either from the Order Complete Page or the Order History Page via modal.
export default function SalesOrderDetails(props: Props): Component {
  const contentToPrint = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({
    contentRef: contentToPrint,
    documentTitle: `Cornell Pump Order ${props.orderNumber}`,
  });

  // Paint color, impeller angle and impeller trim are stored as a comment for the line item. Create the string to be displayed
  function createImpellerConfigurationString(orderItem: OrderItem) {
    let configurationString = "";
    if (orderItem.partNumber.includes("-3-")) {
      if (orderItem.impellerAngle !== "") {
        configurationString =
          configurationString + `Impeller Angle: ${parseInt(orderItem.impellerAngle).toString()}° | `;
      }
      if (orderItem.impellerTrim !== "") {
        configurationString =
          configurationString +
          `Impeller Trim: ${
            parseFloat(orderItem.impellerTrim) === 0 || orderItem.impellerTrim === "Full"
              ? "Full Trim"
              : `${parseFloat(orderItem.impellerTrim).toFixed(2).toString()} inches`
          }`;
      }
    }
    return configurationString;
  }

  return (
    <div className={`card ${props.showHeadersAndFooters ? "border-secondary" : ""} p-2`}>
      {props.showHeadersAndFooters && (
        <div className="card-header">
          <h3 className="text-center">Thank you for your order.</h3>
        </div>
      )}
      <div className="mobile-view d-block d-lg-none">
        <div className="col text-center">
          <img className={`${styles.logo} m-2`} src="/logo.png" alt="Shop Cornell Pump" />
        </div>
        <div className="col text-center">
          <ul style={{ padding: "0" }}>
            <li>{CORNELL_ADDRESS_LINE_ONE}</li>
            <li>{CORNELL_ADDRESS_LINE_TWO}</li>
            <li>{CORNELL_ADDRESS_LINE_THREE}</li>
            <li>{CORNELL_WEB_SITE}</li>
          </ul>
        </div>
        <div className="col text-center">
          <ul style={{ padding: "0" }}>
            <li>
              <div className="col">
                Phone: <span className="font-weight-bold">{SALES_CONTACT_PHONE_NUMBER}</span>
              </div>
            </li>
            <li>
              <div className="col">
                Fax: <span className="font-weight-bold">{SALES_CONTACT_FAX_NUMBER}</span>
              </div>
            </li>
            <li>
              <div className="col">
                Email: <span className="font-weight-bold">{SALES_CONTACT_EMAIL_ADDRESS}</span>
              </div>
            </li>
          </ul>
        </div>
        <div className="col text-center">
          <ul>
            <li>
              <span>
                Ordered on: <span className="font-weight-bold">{formatDashTimeString(props.orderDate)}</span>
              </span>
            </li>
            <li>
              <span>
                Expected Ship Date: <span className="font-weight-bold">{formatDashTimeString(props.shipBy)}</span>
              </span>
            </li>
            <li>
              <span>
                PO#: <span className="font-weight-bold">{props.poNumber}</span>
              </span>
            </li>
            {props.status !== null && props.status !== undefined && props.status !== "" && (
              <li>
                <span>
                  Status: <span className="font-weight-bold">{props.status}</span>
                </span>
              </li>
            )}
          </ul>
        </div>
        <div className="d-none d-md-block">
          <div className={`${styles.orderSummary} card border-secondary m-1`}>
            <div className="p-2">
              <div className="row">
                <div className="col-3"></div>
                <div className="col-6 text-center">
                  <h4>Shipping Address</h4>
                  <ul style={{ padding: "0" }}>
                    {props.shippingCompanyName !== "" && (
                      <li className={styles.content}>{props.shippingCompanyName}</li>
                    )}
                    {props.shippingContactName !== "" && (
                      <li className={styles.content}>{props.shippingContactName}</li>
                    )}
                    <li className={styles.content}>{props.shippingAddressLine1}</li>
                    {props.shippingAddressLine2 !== "" && (
                      <li className={styles.content}>{props.shippingAddressLine2}</li>
                    )}
                    <li className={styles.content}>
                      {props.shippingCity}, {props.shippingState} {props.shippingZipCode}
                    </li>
                    <li className={styles.content}>{props.shippingCountry}</li>
                    {props.shippingPhoneNumber !== "" && (
                      <li className={styles.content}>{props.shippingPhoneNumber}</li>
                    )}
                    {props.specialInstructions !== "" && (
                      <li className={styles.content}>Special Instructions: {props.specialInstructions}</li>
                    )}
                  </ul>
                </div>
                <div className="col-3"></div>
              </div>
            </div>
          </div>
          <div className={`${styles.orderSummary} card border-secondary m-1`}>
            <div className="p-2">
              <div className="row">
                <div className="col-3"></div>
                <div className="col-6 text-center">
                  <h4>Order Summary</h4>
                  <ul style={{ padding: "0" }}>
                    <li className={styles.content}>
                      <div className="col ">
                        Order Number: <span className="font-weight-bold">{props.orderNumber}</span>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="col ">
                        Item(s) Subtotal: $
                        <span className="font-weight-bold">
                          {stringIsValidNumber(props.itemsSubtotal)
                            ? parseFloat(props.itemsSubtotal).toLocaleString("en-US", { minimumFractionDigits: 2 })
                            : "N/A"}
                        </span>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="col ">
                        Company Discount: <span className="font-weight-bold">{props.companyDiscount}%</span>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="col ">
                        Grand Total:{" "}
                        <span className="font-weight-bold">
                          ${parseFloat(props.grandTotal).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </span>
                      </div>
                    </li>
                  </ul>
                </div>
                <div className="col-3"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-block d-md-none">
          <div className={`${styles.orderSummary} card border-secondary m-1`}>
            <div className="p-2">
              <div className="text-center">
                <h4>Shipping Address</h4>
                <ul style={{ padding: "0" }}>
                  {props.shippingCompanyName !== "" && <li className={styles.content}>{props.shippingCompanyName}</li>}
                  {props.shippingContactName !== "" && <li className={styles.content}>{props.shippingContactName}</li>}
                  <li className={styles.content}>{props.shippingAddressLine1}</li>
                  {props.shippingAddressLine2 !== "" && (
                    <li className={styles.content}>{props.shippingAddressLine2}</li>
                  )}
                  <li className={styles.content}>
                    {props.shippingCity}, {props.shippingState} {props.shippingZipCode}
                  </li>
                  <li className={styles.content}>{props.shippingCountry}</li>
                  {props.shippingPhoneNumber !== "" && <li className={styles.content}>{props.shippingPhoneNumber}</li>}
                  {props.specialInstructions !== "" && (
                    <li className={styles.content}>Special Instructions: {props.specialInstructions}</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
          <div className={`${styles.orderSummary} card border-secondary m-1`}>
            <div className="p-2">
              <div className="text-center">
                <h4>Order Summary</h4>
                <ul style={{ padding: "0" }}>
                  <li className={styles.content}>
                    <div className="col">
                      Order Number: <span className="font-weight-bold">{props.orderNumber}</span>
                    </div>
                  </li>
                  <li className={styles.content}>
                    <div className="col">
                      Item(s) Subtotal: $
                      <span className="font-weight-bold">
                        {stringIsValidNumber(props.itemsSubtotal)
                          ? parseFloat(props.itemsSubtotal).toLocaleString("en-US", { minimumFractionDigits: 2 })
                          : "N/A"}
                      </span>
                    </div>
                  </li>
                  <li className={styles.content}>
                    <div className="col">
                      Company Discount: <span className="font-weight-bold">{props.companyDiscount}%</span>
                    </div>
                  </li>
                  <li className={styles.content}>
                    <div className="col">
                      Grand Total:{" "}
                      <span className="font-weight-bold">
                        ${parseFloat(props.grandTotal).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        <div className="pt-3">
          <div className={`${styles.orderItems} card border-secondary`}>
            <div className="table-responsive">
              <table className="table table-sm">
                <thead>
                  <tr>
                    <th className={styles.content} scope="col">
                      Qty.
                    </th>
                    <th className={styles.content} scope="col">
                      Part #
                    </th>
                    <th className={styles.content} scope="col">
                      Desc.
                    </th>
                    <th className={styles.content} scope="col">
                      Paint Color
                    </th>

                    <th className={styles.content} scope="col">
                      Price
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {props.orderItems.map((orderItem) => (
                    <tr
                      key={`${orderItem.partNumber}-${orderItem.paintColor}-${createImpellerConfigurationString(
                        orderItem
                      )}`}
                    >
                      <td className={styles.content}>{parseInt(orderItem.quantity)}</td>
                      <td className={styles.content}>{orderItem.partNumber}</td>
                      <td className={styles.content}>{orderItem.partDescription}</td>
                      <td className={styles.content}>{orderItem.paintColor}</td>
                      <td className={styles.content}>
                        ${parseFloat(orderItem.price).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="m-3 text-center d-block d-md-none">
          <Link to="/dashboard" className="btn btn-primary">
            Return to Dashboard
          </Link>
        </div>

        {SHOW_SALES_ORDERS_NOT_PLACED_DISCLAIMER && (
          <p className={styles.salesOrderDisclaimer}>{SALES_ORDER_NOT_PLACED_DISCLAIMER}</p>
        )}

        <div className="m-3 text-center d-none d-md-block">
          <div className="row p-3 justify-content-between">
            <div className="col text-start">
              <Link to="/dashboard" className="btn btn-primary">
                Return to Dashboard
              </Link>
            </div>
            <div className="col text-end">
              <button className="btn btn-primary" onClick={() => handlePrint()}>
                Print
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="non-mobile-view d-none d-lg-block">
        <div className={`${styles.printContent}`} ref={contentToPrint}>
          <div className="row p-2 justify-content-between">
            <div className="col">
              <ul style={{ padding: "0" }}>
                <li>{CORNELL_ADDRESS_LINE_ONE}</li>
                <li>{CORNELL_ADDRESS_LINE_TWO}</li>
                <li>{CORNELL_ADDRESS_LINE_THREE}</li>
                <li>{CORNELL_WEB_SITE}</li>
              </ul>
            </div>
            <div className="col text-center">
              <img className={`${styles.logo} m-0`} src="/logo.png" alt="Shop Cornell Pump" />
            </div>
            <div className="col">
              <ul style={{ padding: "0" }}>
                <li>
                  <div className="row">
                    <div className="col text-start">Phone:</div>
                    <div className="col text-end font-weight-bold">{SALES_CONTACT_PHONE_NUMBER}</div>
                  </div>
                </li>
                <li>
                  <div className="row">
                    <div className="col text-start">Fax:</div>
                    <div className="col text-end font-weight-bold">{SALES_CONTACT_FAX_NUMBER}</div>
                  </div>
                </li>
                <li>
                  <div className="row">
                    <div className="col text-start">Email:</div>
                    <div className="col text-end font-weight-bold">{SALES_CONTACT_EMAIL_ADDRESS}</div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="row p-2">
            <div className="col">
              <span>
                Ordered on: <span className="font-weight-bold">{formatDashTimeString(props.orderDate)}</span> | PO#:{" "}
                <span className="font-weight-bold">{props.poNumber}</span>
              </span>
            </div>
            {props.status !== null && props.status !== undefined && props.status !== "" && (
              <div className="col text-center">
                Status: <span className="font-weight-bold">{props.status}</span>
              </div>
            )}
            <div className="col text-end">
              Expected Ship Date: <span className="font-weight-bold">{formatDashTimeString(props.shipBy)}</span>
            </div>
          </div>
          <div className={`${styles.orderSummary} card border-secondary`}>
            <div className="p-3">
              <div className="row justify-content-between">
                <div className="col col-lg-5">
                  <h4>Shipping Address</h4>
                  <ul style={{ padding: "0" }}>
                    {props.shippingCompanyName !== "" && (
                      <li className={styles.content}>{props.shippingCompanyName}</li>
                    )}
                    {props.shippingContactName !== "" && (
                      <li className={styles.content}>{props.shippingContactName}</li>
                    )}
                    <li className={styles.content}>{props.shippingAddressLine1}</li>
                    {props.shippingAddressLine2 !== "" && (
                      <li className={styles.content}>{props.shippingAddressLine2}</li>
                    )}
                    <li className={styles.content}>
                      {props.shippingCity}, {props.shippingState} {props.shippingZipCode}
                    </li>
                    <li className={styles.content}>{props.shippingCountry}</li>
                    {props.shippingPhoneNumber !== "" && (
                      <li className={styles.content}>{props.shippingPhoneNumber}</li>
                    )}
                    {props.specialInstructions !== "" && (
                      <li className={styles.content}>Special Instructions: {props.specialInstructions}</li>
                    )}
                  </ul>
                </div>
                <div className="col col-lg-5">
                  <h4>Order Summary</h4>
                  <ul style={{ padding: "0" }}>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col text-start">Order Number:</div>
                        <div className="col text-end font-weight-bold">{props.orderNumber}</div>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col text-start">Item(s) Subtotal:</div>
                        <div className="col text-end font-weight-bold">
                          $
                          {stringIsValidNumber(props.itemsSubtotal)
                            ? parseFloat(props.itemsSubtotal).toLocaleString("en-US", { minimumFractionDigits: 2 })
                            : "N/A"}
                        </div>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col-9 text-start">{props.companyName} Discount:</div>{" "}
                        <div className="col text-end font-weight-bold">{props.companyDiscount}%</div>
                      </div>
                    </li>
                    <li className={styles.content}>
                      <div className="row">
                        <div className="col-7 text-start">Grand Total:</div>
                        <div className="col text-end font-weight-bold">
                          $
                          {stringIsValidNumber(props.grandTotal)
                            ? parseFloat(props.grandTotal).toLocaleString("en-US", { minimumFractionDigits: 2 })
                            : "N/A"}
                        </div>
                      </div>
                    </li>
                    {props.terms !== null && props.terms !== undefined && props.terms !== "" && (
                      <li className={styles.content}>
                        <div className="row">
                          <div className="col-7 text-start">Terms:</div>
                          <div className="col text-end font-weight-bold">{props.terms}</div>
                        </div>
                      </li>
                    )}
                    {props.fob !== null && props.fob !== undefined && props.fob !== "" && (
                      <li className={styles.content}>
                        <div className="row">
                          <div className="col-7 text-start">FOB:</div>
                          <div className="col text-end font-weight-bold">{props.fob}</div>
                        </div>
                      </li>
                    )}
                    {props.salesPerson !== null && props.salesPerson !== undefined && props.salesPerson !== "" && (
                      <li className={styles.content}>
                        <div className="row">
                          <div className="col-7 text-start">Sales Person:</div>
                          <div className="col text-end font-weight-bold">{props.salesPerson}</div>
                        </div>
                      </li>
                    )}
                    {props.shipVia !== null && props.shipVia !== undefined && props.shipVia !== "" && (
                      <li className={styles.content}>
                        <div className="row">
                          <div className="col-7 text-start">Ship Via:</div>
                          <div className="col text-end font-weight-bold">{props.shipVia}</div>
                        </div>
                      </li>
                    )}
                    {props.billingType !== null && props.billingType !== undefined && props.billingType !== "" && (
                      <li className={styles.content}>
                        <div className="row">
                          <div className="col-7 text-start">Billing Type:</div>
                          <div className="col text-end font-weight-bold">{props.billingType}</div>
                        </div>
                      </li>
                    )}
                    {props.trackingNumber !== null &&
                      props.trackingNumber !== undefined &&
                      props.trackingNumber !== "" && (
                        <li className={styles.content}>
                          <div className="row">
                            <div className="col-7 text-start">Tracking Number:</div>
                            <div className="col text-end font-weight-bold">{props.trackingNumber}</div>
                          </div>
                        </li>
                      )}
                  </ul>
                </div>
              </div>
            </div>
          </div>
          <div className="pt-3">
            <div className={`${styles.orderItems} card border-secondary`}>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead>
                    <tr>
                      <th className={styles.content} scope="col">
                        Qty.
                      </th>
                      <th className={styles.content} scope="col">
                        Part Number
                      </th>
                      <th className={styles.content} scope="col">
                        Description
                      </th>
                      <th className={styles.content} scope="col">
                        Paint Color
                      </th>
                      <th className={styles.content} scope="col">
                        Impeller Configuration
                      </th>
                      <th className={styles.content} scope="col">
                        Unit Price
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {props.orderItems.map((orderItem) => (
                      <tr
                        key={`${orderItem.partNumber}-${orderItem.paintColor}-${createImpellerConfigurationString(
                          orderItem
                        )}`}
                      >
                        <td className={styles.content}>{parseInt(orderItem.quantity)}</td>
                        <td className={styles.content}>{orderItem.partNumber}</td>
                        <td className={styles.content}>{orderItem.partDescription}</td>
                        <td className={styles.content}>{orderItem.paintColor}</td>
                        <td className={styles.content}>{createImpellerConfigurationString(orderItem)}</td>
                        <td className={styles.content}>
                          ${parseFloat(orderItem.price).toLocaleString("en-US", { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        {SHOW_SALES_ORDERS_NOT_PLACED_DISCLAIMER && (
          <p className={styles.salesOrderDisclaimer}>{SALES_ORDER_NOT_PLACED_DISCLAIMER}</p>
        )}
        {props.showHeadersAndFooters && (
          <div className="row p-3 justify-content-between">
            <div className="col-4 text-start">
              <Link to="/dashboard" className="btn btn-primary">
                Return to Dashboard
              </Link>
            </div>
            <div className="col-4 text-end">
              <button className="btn btn-primary" onClick={() => handlePrint()}>
                Print
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

interface Props {
  showHeadersAndFooters: boolean;
  orderNumber: string;
  poNumber: string;
  loading: boolean;
  shippingCountry: string;
  shippingCompanyName: string;
  shippingAddressLine1: string;
  shippingAddressLine2: string;
  shippingContactName: string;
  shippingPhoneNumber: string;
  shippingCity: string;
  shippingState: string;
  shippingZipCode: string;
  specialInstructions: string;
  itemsSubtotal: string;
  shipBy: string;
  grandTotal: string;
  orderDate: string;
  companyDiscount: string;
  companyName: string;
  orderItems: OrderItem[];
  terms?: string;
  fob?: string;
  salesPerson?: string;
  shipVia?: string;
  billingType?: string;
  trackingNumber?: string;
  status?: string;
  onChangeLoading: (loading: boolean) => void;
  onChangeFailedToLoad: (failedToLoad: boolean) => void;
  onChangeErrorMessage: (errorMessage: string) => void;
}

interface OrderItem {
  partNumber: string;
  partDescription: string;
  paintColor: string;
  impellerTrim: string;
  impellerAngle: string;
  price: string;
  quantity: string;
}
